/**
 * Copyright IBM Corp. 2021, 2024-2025
 *
 * This source code is licensed under the IBM Public License Version 1.0 found in the
 * LICENSE file in the root directory of this source tree.
 */

import { TriModel, getTriAppConfig } from "../..";

const OFFICE_365_MODEL_AND_VIEW = "triOffice365";
const OFFICE_365_INSTANCE_ID = "-1";
const OFFICE_365_CONFIG_DS_NAME = "config";
const TRIRIGA_GET_ACCESS_TOKEN_URL = "/api/p/v1/office365/Token";
const TRIRIGA_GET_REFRESH_TOKEN_URL = "/api/p/v1/office365/RefreshToken";
const TRIRIGA_GET_PROFILE_URL = "/api/p/v1/office365/Profile";

const REFRESH_TOKEN_NOT_FOUND = "RefreshTokenNotFound";

export async function getRequestAuthorizationCodeURL(profileName) {
  try {
    const oauthProfile = await getOAuthProfile();

    if (oauthProfile != null) {
      const authCodeUrl = new URL(oauthProfile.oauthAuthorizeURL);
      authCodeUrl.searchParams.set(
        "client_id",
        oauthProfile.oauthClientId
      );
      authCodeUrl.searchParams.set(
        "redirect_uri",
        oauthProfile.oauthRedirectURL
      );
      authCodeUrl.searchParams.set("response_type", "code");
      authCodeUrl.searchParams.set("response_mode", "query");
      if (oauthProfile.oauthScope) {
        authCodeUrl.searchParams.set("scope", oauthProfile.oauthScope);
      }
      authCodeUrl.searchParams.set("state", btoa(profileName));
      return authCodeUrl;
    } else {
      throw new OauthException("AUTH_PROFILE_NOT_FOUND");
    }
  } catch (error) {
    throw new OauthException("AUTH_PROFILE_FAILED", error);
  }
}

export function receiveAuthorizationCode() {
  const currentUrl = new URL(window.location.href);
  const code = currentUrl.searchParams.get("code");
  const state = currentUrl.searchParams.get("state");
  let profileName = null;
  if (state != null) {
    profileName = atob(state);
  }
  const error = currentUrl.searchParams.get("error");
  const errorDescription = currentUrl.searchParams.get("error_description");
  if (error != null) {
    throw new OauthException(
      "AUTH_CODE_FAILED",
      error + "-" + errorDescription
    );
  }
  if (code == null) {
    throw new OauthException("AUTH_CODE_FAILED");
  }
  return { code, profileName };
}

export async function redeemRefreshToken(profileName) {
  const redeemRefreshTokenUrl = new URL(
    `${getTriAppConfig().tririgaUrl}${TRIRIGA_GET_REFRESH_TOKEN_URL}`
  );
  redeemRefreshTokenUrl.searchParams.set("name", profileName);

  const requestOptions = {
    method: "POST",
    credentials: "include",
  };
  const request = new Request(redeemRefreshTokenUrl, requestOptions);
  const response = await fetch(request);
  if (!response.ok) {
    throw new OauthException("REFRESH_TOKEN_FAILED", response.status);
  }
  const token = await response.json();
  if (
    token.ErrorCode ||
    token.errorCode ||
    token.Mesage === REFRESH_TOKEN_NOT_FOUND
  ) {
    throw new OauthException(
      "REFRESH_TOKEN_FAILED",
      token.ErrorCode || token.errorCode || token.Mesage
    );
  }
  return token;
}

export async function getAccessToken(authCode, profileName) {
  const getAccessTokenUrl = new URL(
    `${getTriAppConfig().tririgaUrl}${TRIRIGA_GET_ACCESS_TOKEN_URL}`
  );
  getAccessTokenUrl.searchParams.set("authcode", authCode);
  getAccessTokenUrl.searchParams.set("name", profileName);
  const requestOptions = {
    method: "POST",
    credentials: "include",
  };
  const request = new Request(getAccessTokenUrl, requestOptions);
  const response = await fetch(request);
  if (!response.ok) {
    const contentType = response.headers.get("Content-Type");
    if (contentType && !contentType.includes("application/json")) {
      const results = await response.text();
      const matchResults = results.match(
        /The Microsoft email (.+?) and the TRIRIGA user email (.+?) must match/
      );
      if (matchResults) {
        throw new OauthException("EMAIL_MUST_MATCH", {
          microsoftEmail: matchResults[1],
          tririgaEmail: matchResults[2],
        });
      }
    }

    throw new OauthException("ACCESS_CODE_FAILED", response.status);
  }
  const token = await response.json();
  if (
    token.ErrorCode ||
    token.errorCode ||
    token.Mesage === REFRESH_TOKEN_NOT_FOUND
  ) {
    throw new OauthException(
      "ACCESS_CODE_FAILED",
      token.ErrorCode || token.errorCode || token.Mesage
    );
  }
  return token;
}

/**
 * Get OAUTH Profile from Query
 *
 * @returns
 */
async function getOAuthProfileFromQuery() {
  const model = new TriModel(
    OFFICE_365_MODEL_AND_VIEW,
    OFFICE_365_INSTANCE_ID
  );
  const page = { from: 0, size: 1 };
  const filters = [
    {
      name: "name",
      operator: "equals",
      value: process.env.REACT_APP_TRIRIGA_OAUTH_PROFILE_NAME,
    },
  ];
  const query = { filters, page };
  const response = await model.getRecord(OFFICE_365_CONFIG_DS_NAME, query);
  return response.data && response.data.length > 0 ? response.data[0] : null;
}

/**
 * Get OAUTH Profile from API
 *
 * @returns
 */
async function getOAuthProfileFromAPI() {
    const getMsOAuthProfileUrl = new URL(
        `${getTriAppConfig().tririgaUrl}${TRIRIGA_GET_PROFILE_URL}`
    );
    const requestOptions = {
        method: "POST",
        credentials: "include",
    };
    const request = new Request(getMsOAuthProfileUrl, requestOptions);
    const response = await fetch(request);
    if (!response.ok) {
        return response.text().then((errMessage) => {
            throw new OauthException(
                "GET_OAUTH_PROFILE_FAILED",
                response.status,
                errMessage
            );
        });
    }

    return response.json();
}

/**
 * Get OAUTH Profile
 *
 * @returns
 */
export async function getOAuthProfile() {
  if (
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_TRIRIGA_OAUTH_PROFILE_NAME
  ) {
    const profile = await getOAuthProfileFromQuery();

    if (!profile) {
      return null;
    }

    return {
      name: profile.name,
      oauthAuthorizeURL: profile.authorizeURL,
      oauthClientId: profile.key,
      oauthRedirectURL: profile.redirectURL,
      oauthScope: profile.scope,
    };
  }

  return getOAuthProfileFromAPI();
}

export function OauthException(errorCode, error, message) {
    this.errorCode = errorCode;
    this.error = error;
    this.message = message;
}
